import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
} from '@apollo/client';

import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 }                   from 'js-sha256';
import { setContext }               from '@apollo/client/link/context';

function ApolloWrapper({ token, children }) {
  const httpLink = createHttpLink({
    uri: '/gql/v1',
  });

  const operationStateDictionary = useRef({});

  const updateOperationState = (operationRoot, query) => {
    const isMutation = query.definitions[0].operation === 'mutation';

    if (isMutation) {
      operationStateDictionary.current[operationRoot] ??= { mutationCount: 0 };
      operationStateDictionary.current[operationRoot].mutationCount += 1;
    }
  };

  const getStatefulOperationName = (operationName, operationRoot) => {
    const { mutationCount } = operationStateDictionary.current[operationRoot] || {};
    return mutationCount ? `${operationName}-${mutationCount}` : operationName;
  };

  const authLink = setContext(({ query, variables, operationName }, { headers }) => {
    let idkey = {};
    // skip Idempotency-Key that ends with NoId
    if (!operationName.endsWith('NoId')) { // no idempotency key
      const operationRoot = operationName.split(/(Create|Fetch|Update|Delete)/)[0];
      updateOperationState(operationRoot, query);
      const statefulOperationName = getStatefulOperationName(operationName, operationRoot);

      idkey = {
        'Idempotency-Key': sha256(JSON.stringify({
          token, query, variables, statefulOperationName,
        })),
      };
    }

    return ({
      // get the authentication token from local storage if it exists
      // return the headers to the context so httpLink can read them
      headers: {
        ...headers,
        Authorization: token,
        ...idkey,
      },
    });
  });

  const client = new ApolloClient({
    link:  authLink.concat(httpLink),
    cache: new InMemoryCache(),
  });

  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  );
}

ApolloWrapper.defaultProps = {
  token:    undefined,
  children: undefined,
};

ApolloWrapper.propTypes = {
  token:    PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export default ApolloWrapper;
